import { IMAGE_FILE_SUPPORTED_FORMATS } from "$constants/images"
import { ImagesDimensions } from "$types/assets"
import { getFileType } from "../utils"

declare global {
  interface Window {
    IMAGE_DIMENSIONS: ImagesDimensions
  }
}

type ResizeImageOptions = {
  imageFile: File
  dimensions?: (keyof ImagesDimensions)[]
  customWidth?: number
}

type ResizeImageResults = {
  success: true
  results: { 
    [key in keyof ImagesDimensions]?: File
  } & {
    custom?: File
  }
  error: null
} | {
  success: false,
  results: null
  error: string
}

const checkImageType = async(file: File) => {
  const validImageTypes = IMAGE_FILE_SUPPORTED_FORMATS

  let fileType: string | undefined = await getFileType(file)

  return validImageTypes.includes(fileType as never)
}

const resizeImage = async(options: ResizeImageOptions): Promise<ResizeImageResults> => {
  const { imageFile } = options

  const dimensions = options.dimensions ?? Object.keys(window.IMAGE_DIMENSIONS) as (keyof ImagesDimensions)[]

  if (!await checkImageType(imageFile)) {
    return {
      success: false,
      results: null,
      error: `Invalid image type. Allowed types are: ${IMAGE_FILE_SUPPORTED_FORMATS.join(", ")}`
    }
  }
  
  const img = new Image()
  img.src = URL.createObjectURL(imageFile)
  await img.decode()

  const results: ResizeImageResults["results"] = {}
  for (const dimension of dimensions) {
    if (!window.IMAGE_DIMENSIONS[dimension]) {
      return {
        success: false,
        results: null,
        error: `Invalid dimension: ${dimension}. Allowed dimensions are: ${Object.keys(window.IMAGE_DIMENSIONS).join(", ")}`
      }
    }

    const canvas = document.createElement("canvas")
    const ctx = canvas.getContext("2d")

    if (!ctx) {
      return {
        success: false,
        results: null,
        error: "Could not get canvas context"
      }
    }

    const aspectRatio = img.width / img.height
    let width = window.IMAGE_DIMENSIONS[dimension]
    let height = Math.round(width / aspectRatio)
    canvas.width = width
    canvas.height = height
    ctx?.drawImage(img, 0, 0, width, height)

    const base64 = canvas.toDataURL("image/webp", 0.9)
    const file = await (await fetch(base64)).blob()
    
    results[dimension] = new File([file], imageFile.name, { type: imageFile.type })
  }

  if (options.customWidth) {
    const canvas = document.createElement("canvas")
    const ctx = canvas.getContext("2d")

    if (!ctx) {
      return {
        success: false,
        results: null,
        error: "Could not get canvas context"
      }
    }

    const aspectRatio = img.width / img.height
    let width = options.customWidth
    let height = Math.round(width / aspectRatio)
    canvas.width = width
    canvas.height = height
    ctx?.drawImage(img, 0, 0, width, height)

    const base64 = canvas.toDataURL("image/webp", 0.9)
    const file = await (await fetch(base64)).blob()
    
    results["custom"] = new File([file], imageFile.name, { type: imageFile.type })
  }
  
  return {
    success: true,
    results,
    error: null
  }
}

export {
  resizeImage
}
